import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { Button } from "../components/ui/button";
import { RURAL_PILLARS } from "../lib/data";

const Icon = ({ name, className }) => { const C = Icons[name] || Icons.Circle; return <C className={className} />; };
const fadeUp = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.45, delay: i * 0.05 } }) };

const IMG = {
  farmer: "https://images.unsplash.com/photo-1696371269777-88d1ce71642c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  artisan: "https://images.unsplash.com/photo-1721508490084-1b1de5b230d4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  market: "https://images.unsplash.com/photo-1560579183-61c3f4596a85?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
};

export default function RuralConnect() {
  return (
    <div data-testid="rural-connect-page" className="bg-cream-100 text-soil-700 pt-24 pb-24">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta-600 mb-3">Rural Connect</div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-forest-800">Connecting Bharat's local enterprise with modern markets</h1>
            <p className="mt-4 text-lg text-soil-600">Built around the idea of connecting local enterprise — farmers, artisans, MSMEs and neighbourhood retailers — with wider opportunities and a modern digital platform.</p>
            <div className="flex flex-wrap gap-2 mt-6">{["Support Local", "Connect Local", "Grow Local", "Think Global", "Swadeshi Spirit"].map((t) => <span key={t} className="text-sm px-3 py-1.5 rounded-full bg-forest-100 text-forest-700 font-medium">{t}</span>)}</div>
            <Link to="/register" data-testid="rural-join-cta" className="inline-block mt-7"><Button className="forest-gradient text-cream-50 font-bold h-11 px-6 rounded-full">Join the Ecosystem</Button></Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <img src={IMG.farmer} alt="Indian farmer" loading="lazy" className="col-span-2 h-52 w-full object-cover rounded-2xl border border-cream-300" />
            <img src={IMG.market} alt="Local market" loading="lazy" className="h-36 w-full object-cover rounded-2xl border border-cream-300" />
            <img src={IMG.artisan} alt="Artisan craftsmanship" loading="lazy" className="h-36 w-full object-cover rounded-2xl border border-cream-300" />
          </div>
        </div>
      </section>

      <div className="weave-divider" />

      {/* PILLARS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <div className="text-center"><div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta-600 mb-3">Who We Connect</div><h2 className="font-heading text-2xl sm:text-3xl font-bold text-forest-800">From Local to Global</h2><p className="text-soil-600 mt-2">Rooted in Bharat. Connected to opportunity.</p></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {RURAL_PILLARS.map((p, i) => (
            <motion.div key={p.t} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="card-cream rounded-2xl p-6">
              <div className="w-11 h-11 rounded-xl bg-terracotta-100 flex items-center justify-center mb-3"><Icon name={p.icon} className="w-5 h-5 text-terracotta-600" /></div>
              <div className="font-heading text-lg font-bold text-forest-800">{p.t}</div>
              <div className="text-soil-500 text-sm mt-1">{p.d}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* BHARAT BAND */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="rounded-3xl forest-gradient text-cream-50 p-8 sm:p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 bharat-pattern opacity-40" />
          <div className="relative">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold">Swadeshi Spirit. Modern Platform.</h2>
            <p className="text-cream-200/90 mt-3 max-w-2xl mx-auto">We celebrate local entrepreneurship with dignity and aspiration — bringing India's businesses onto one connected, modern ecosystem.</p>
            <Link to="/opportunity" data-testid="rural-network-cta" className="inline-block mt-7"><Button className="terracotta-gradient text-cream-50 font-bold h-11 px-6 rounded-full">Become a Business Associate</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
